import React, { useState, useCallback, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import CheckLengthBadge from './CheckLengthBadge';

const client = generateClient({ authMode: 'userPool' });

const CATEGORY_ORDER = ['FLATHEAD', 'BREAM', 'JEWFISH', 'MANGROVE_JACK', 'WHITING', 'TAILOR', 'TREVALLY', 'MYSTERY_FISH'];
const CATEGORY_LABELS: Record<string, string> = {
  FLATHEAD: 'Flathead',
  BREAM: 'Bream',
  JEWFISH: 'Jewfish',
  MANGROVE_JACK: 'Mangrove Jack',
  WHITING: 'Whiting',
  TAILOR: 'Tailor',
  TREVALLY: 'Trevally',
  MYSTERY_FISH: 'Mystery Fish 🎣',
};

const POSITION_STYLES: Record<number, string> = {
  1: 'bg-yellow-50 border-l-4 border-l-yellow-400',
  2: 'bg-gray-50 border-l-4 border-l-gray-300',
  3: 'bg-amber-50 border-l-4 border-l-amber-400',
};

interface LeaderboardViewProps {
  eventId: string;
}

const LeaderboardView: React.FC<LeaderboardViewProps> = ({ eventId }) => {
  const [leaderboard, setLeaderboard] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLeaderboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await (client.queries as any).calculateLeaderboard({ eventId });
      const data = parseLambdaResponse(result?.data);
      setLeaderboard(data);
    } catch (err: any) {
      console.error('Error loading leaderboard:', err);
      setError('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { loadLeaderboard(); }, [loadLeaderboard]);

  if (loading) {
    return <div className="text-center py-8 text-gray-500">Loading leaderboard...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        {error}
        <button onClick={loadLeaderboard} className="ml-3 underline">Retry</button>
      </div>
    );
  }

  // These key names must match calculate-leaderboard/handler.ts's response
  // exactly. They previously read `categories`/`fishCounts`/`overallFishCount`,
  // which the Lambda has never returned — so every category rendered empty and
  // the totals always showed 0. The Lambda returns `categoryRankings`,
  // `competitorFishCounts` and `totalFishCount`.
  const categoryRankings = leaderboard?.categoryRankings || {};
  const fishCounts = leaderboard?.competitorFishCounts || {};
  const overallFishCount = leaderboard?.totalFishCount || 0;

  // Mystery fish comes back as its own top-level array, not inside
  // categoryRankings, so it needs merging in to render under the
  // MYSTERY_FISH slot that CATEGORY_ORDER already reserves for it.
  const mysteryFishRankings = leaderboard?.mysteryFishRankings || [];
  const categories: Record<string, any[]> = {
    ...categoryRankings,
    ...(mysteryFishRankings.length > 0 ? { MYSTERY_FISH: mysteryFishRankings } : {}),
  };

  // Admin-only (A3, A8): catches excluded from placings for an invalid
  // length, and placed catches with no member identity to pay champion
  // points to. The server only returns these to a userPool admin caller.
  const excludedCatches: Array<{ id: string; competitorName?: string | null; species: string; lengthCm: number; reason: string }> = leaderboard?.excludedCatches || [];
  const championNoIdentity: Array<{ id: string; competitorName?: string | null; species: string; rank: number }> = leaderboard?.championNoIdentity || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900">Leaderboard</h3>
        <button
          onClick={loadLeaderboard}
          className="px-4 py-2 text-sm font-medium text-fishing-blue bg-blue-50 hover:bg-blue-100 rounded-md"
        >
          Refresh
        </button>
      </div>

      {/* Per-category sections */}
      {CATEGORY_ORDER.map((catKey) => {
        const entries = categories[catKey];
        if (!entries || entries.length === 0) return null;
        return (
          <div key={catKey} className="mb-8">
            <h4 className="text-md font-semibold text-gray-800 mb-3">
              {CATEGORY_LABELS[catKey] || catKey}
            </h4>
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {entries.map((entry: any, idx: number) => {
                const position = idx + 1;
                const style = POSITION_STYLES[position] || '';
                return (
                  <div key={entry.competitorName + idx} className={`flex items-center justify-between px-4 py-3 border-b border-gray-100 last:border-b-0 ${style}`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold ${
                        position === 1 ? 'bg-yellow-400 text-white' :
                        position === 2 ? 'bg-gray-300 text-white' :
                        position === 3 ? 'bg-amber-500 text-white' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {position}
                      </span>
                      <span className="font-medium text-gray-900">{entry.competitorName}</span>
                    </div>
                    <span className="flex items-center gap-2">
                      <CheckLengthBadge catchItem={{ species: catKey, lengthCm: entry.lengthCm }} />
                      <span className="text-lg font-bold text-fishing-blue">{entry.lengthCm}cm</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Fish counts */}
      <div className="bg-white shadow rounded-lg p-4 mt-6">
        <h4 className="text-md font-semibold text-gray-800 mb-3">Fish Counts</h4>
        {Object.keys(fishCounts).length > 0 ? (
          <div className="space-y-1">
            {Object.entries(fishCounts).map(([name, count]: [string, any]) => (
              <div key={name} className="flex justify-between text-sm">
                <span className="text-gray-700">{name}</span>
                <span className="font-medium text-gray-900">{count} fish</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No fish counts available.</p>
        )}
        <div className="mt-3 pt-3 border-t border-gray-200 flex justify-between font-semibold">
          <span className="text-gray-900">Overall Total</span>
          <span className="text-fishing-blue">{overallFishCount} fish</span>
        </div>
      </div>

      {/* A3: catches excluded from placings for an invalid length, admin only. */}
      {excludedCatches.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mt-6 border-l-4 border-l-red-400">
          <h4 className="text-md font-semibold text-gray-800 mb-3">Excluded catches</h4>
          <p className="text-xs text-gray-500 mb-2">These catches are not in the rankings above because of an invalid length.</p>
          <div className="space-y-1">
            {excludedCatches.map((c) => (
              <div key={c.id} className="flex justify-between text-sm">
                <span className="text-gray-700">{c.competitorName || 'Unnamed angler'}: {CATEGORY_LABELS[c.species] || c.species}, {c.lengthCm}cm</span>
                <span className="font-medium text-red-700">Excluded: invalid length</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* A8: placed catches with no resolvable member identity, admin only. */}
      {championNoIdentity.length > 0 && (
        <div className="bg-white shadow rounded-lg p-4 mt-6 border-l-4 border-l-amber-400">
          <h4 className="text-md font-semibold text-gray-800 mb-3">No member identity</h4>
          <p className="text-xs text-gray-500 mb-2">These catches earn no champion points: no member identity.</p>
          <div className="space-y-1">
            {championNoIdentity.map((c) => (
              <div key={c.id} className="flex justify-between text-sm">
                <span className="text-gray-700">{c.competitorName || 'Unnamed angler'}: {CATEGORY_LABELS[c.species] || c.species}</span>
                <span className="font-medium text-gray-900">Placed {c.rank}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaderboardView;
